import { Stack, useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePoliciesQuery } from '../../../api/getPolicies';
import { getPolicyMarketingCards } from '../../../components/InfoCards';
import PhoneNumberLink from '../../../components/styledComponents/PhoneNumberLink';
import StatusAlert from '../../../components/styledComponents/StatusAlert';
import { StatusType } from '../../../constants/enums';
import useAnalytics from '../../../hooks/useAnalytics';
import useHideChat from '../../../hooks/useHideChat';
import useUpdateTitle from '../../../hooks/useUpdateTitle';
import { useSnackbarsContext } from '../../../store/SnackbarProvider';
import { useChatContext } from '../../../store/chat';
import theme from '../../../styles/theme';
import PolicyCard from './PolicyCard';
import { useBillsQuery } from '../../../api/getBills';
import CircularProgress from '../../../components/CircularProgress/CircularProgress';

export interface PolicyCardProps {
  ubiEnabled?: boolean;
  unenrolledPaperlessPolicy?: boolean;
}

const disableChat = import.meta.env.VITE_CHAT_DISABLED === 'true';

const PolicyCards = ({
  ubiEnabled,
  unenrolledPaperlessPolicy,
}: PolicyCardProps) => {
  useUpdateTitle('Policies');
  const { closeSnackbar } = useSnackbarsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatParamOpened = searchParams.get('chat') === 'opened';
  const { track } = useAnalytics();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
  const { data, isLoading, error } = usePoliciesQuery();
  const { data: billsData } = useBillsQuery();

  const chatButton: HTMLButtonElement | null =
    document.querySelector<HTMLButtonElement>(
      "[data-selector='GUIDE_CHANNEL_BUTTON']"
    );

  const { startChat } = useChatContext();
  const hideChat = useHideChat();

  useEffect(() => {
    const openChat = chatParamOpened;

    if (openChat && chatButton && !disableChat && !hideChat) {
      startChat(track);
      searchParams.delete('chat');
      setSearchParams(searchParams);
    }
  }, [chatParamOpened, chatButton, hideChat]);

  const policies = data?.policies;

  const crossSellInfo = data?.crossSellInfo;
  const showAutoHomeCard = crossSellInfo?.crossSellEligible && !!billsData;

  const mobileCards = policies?.flatMap((e, idx) => {
    const PolicyMarketingCards = getPolicyMarketingCards(
      ubiEnabled,
      unenrolledPaperlessPolicy,
      showAutoHomeCard
    );
    const MarketingCard = PolicyMarketingCards[idx] ?? null;

    if (idx < PolicyMarketingCards.length && idx >= policies.length - 1) {
      return [
        <MarketingCard
          key={`policies-marketing-${idx}`}
          data-testid={`policies-marketing-${idx}`}
        />,
        <PolicyCard
          key={`policy-${idx}-${e.policyNumber}`}
          policy={e}
          data-testid={`policy-card-${e.policyNumber}`}
        />,
        PolicyMarketingCards.slice(idx + 1).map(
          (PolicyMarketingCard, index) => (
            <PolicyMarketingCard
              key={`policies-marketing-${index}`}
              data-testid={`policies-marketing-${index}`}
            />
          )
        ),
      ];
    } else {
      return PolicyMarketingCards[idx]
        ? [
            <MarketingCard
              key={`policies-marketing-${idx}`}
              data-testid={`policies-marketing-${idx}`}
            />,
            <PolicyCard
              key={`policy-${idx}-${e.policyNumber}`}
              policy={e}
              data-testid={`policy-card-${e.policyNumber}`}
            />,
          ]
        : [
            <PolicyCard
              key={`policy-${idx}-${e.policyNumber}`}
              policy={e}
              data-testid={`policy-card-${e.policyNumber}`}
            />,
          ];
    }
  });

  useEffect(
    () => () => {
      closeSnackbar('linkPolicySuccess');
      closeSnackbar('linkPolicyError');
      closeSnackbar('documentOpenError');
    },
    []
  );

  if (isLoading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: 1,
        }}
      >
        <CircularProgress size={32} />
      </Stack>
    );
  }

  if (error) {
    return (
      <StatusAlert
        key="bills-load-error"
        open
        title="This information is currently unavailable."
        text={
          <>
            Try again later or call &nbsp;
            <PhoneNumberLink phoneNumber="8889805650" />.
          </>
        }
        status={StatusType.ERROR}
      />
    );
  }

  return (
    <>
      {isMobile
        ? mobileCards
        : policies?.map((policy, idx) => (
            <PolicyCard
              key={`policy-${idx}-${policy.policyNumber}`}
              policy={policy}
              data-testid={`policy-card-${policy.policyNumber}`}
            />
          ))}
    </>
  );
};

export default PolicyCards;
